import React, { useState, useEffect } from 'react';
import { X, Info } from 'lucide-react';
import { ProcedureCatalogItem, ProcedureCatalogCreate, ProcedureCatalogUpdate } from '../../utils/procedureCatalogApi';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../config/api';
import { useEscapeKey } from '../../hooks/useEscapeKey';

interface ProcedureCatalogItemModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: ProcedureCatalogCreate | ProcedureCatalogUpdate) => Promise<void>;
    onHardDelete?: (itemId: string) => Promise<void>;
    item: ProcedureCatalogItem | null;
    mode: 'create' | 'edit';
}

interface ReferenceInfo {
    procedure_orders?: number;
    [key: string]: number | undefined;
}

const PROCEDURE_TYPES = [
    { value: 'EXAM', label: 'Physical Exam' },
    { value: 'IMAGING', label: 'Imaging (X-ray, Ultrasound)' },
    { value: 'VITALS', label: 'Vitals (BP, ECG, Glucose)' },
    { value: 'OTHER', label: 'Other Procedure' },
];

const ProcedureCatalogItemModal: React.FC<ProcedureCatalogItemModalProps> = ({
    isOpen,
    onClose,
    onSave,
    onHardDelete,
    item,
    mode
}) => {
    const { user } = useAuth();
    const [formData, setFormData] = useState({
        name: '',
        procedure_type: 'EXAM',
        description: '',
        default_duration_minutes: 15,
        requires_equipment: false,
        equipment_notes: '',
        notes: '',
        is_stock_tracked: false,
        display_order: 0
    });
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [references, setReferences] = useState<ReferenceInfo | null>(null);
    const [loadingRefs, setLoadingRefs] = useState(false);

    useEscapeKey(onClose, isOpen);

    // Can only hard delete custom (non-standard) items in edit mode
    const canHardDelete = mode === 'edit' && item && !item.is_standard && onHardDelete;
    const hasReferences = references && Object.values(references).some(v => v && v > 0);

    const handleHardDelete = async () => {
        if (!item || !onHardDelete) return;

        setDeleting(true);
        setError(null);

        try {
            await onHardDelete(item.id);
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to delete item');
            setShowDeleteConfirm(false);
        } finally {
            setDeleting(false);
        }
    };

    // Check references when modal opens for custom items
    useEffect(() => {
        const checkReferences = async () => {
            if (!isOpen || !canHardDelete || !item || !user?.practice_id) {
                setReferences(null);
                return;
            }

            setLoadingRefs(true);
            try {
                const token = localStorage.getItem('access_token');
                const response = await fetch(
                    `${API_BASE_URL}/api/v1/practices/${user.practice_id}/procedures/${item.id}/references`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (response.ok) {
                    const refs = await response.json();
                    setReferences(refs);
                }
            } catch {
                setReferences(null);
            } finally {
                setLoadingRefs(false);
            }
        };

        checkReferences();
    }, [isOpen, canHardDelete, item?.id, user?.practice_id]);

    useEffect(() => {
        if (item) {
            setFormData({
                name: item.name,
                procedure_type: item.procedure_type || 'EXAM',
                description: item.description || '',
                default_duration_minutes: item.default_duration_minutes || 15,
                requires_equipment: item.requires_equipment || false,
                equipment_notes: item.equipment_notes || '',
                notes: item.notes || '',
                is_stock_tracked: item.is_stock_tracked ?? false,
                display_order: item.display_order || 0
            });
        } else {
            setFormData({
                name: '',
                procedure_type: 'EXAM',
                description: '',
                default_duration_minutes: 15,
                requires_equipment: false,
                equipment_notes: '',
                notes: '',
                is_stock_tracked: false,
                display_order: 0
            });
        }
        setError(null);
        setShowDeleteConfirm(false);
    }, [item, mode, isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);
        try {
            await onSave({
                ...formData,
                default_duration_minutes: formData.default_duration_minutes || undefined
            });
            onClose();
        } catch (error: any) {
            console.error('Failed to save:', error);
            setError(error.message || 'Failed to save item');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                    <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
                </div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg leading-6 font-medium text-gray-900">
                                {mode === 'create' ? (item?.is_standard ? 'Override Standard Procedure' : 'Add Custom Procedure') : 'Edit Procedure'}
                            </h3>
                            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
                                {error}
                            </div>
                        )}

                        {item?.is_standard && mode === 'create' && (
                            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-700">
                                You're creating a practice-specific version of "{item.name}". This will override the standard item for your practice.
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Procedure Name *</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    placeholder="e.g., Physical Exam - Wellness"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Procedure Type</label>
                                <select
                                    value={formData.procedure_type}
                                    onChange={(e) => setFormData({ ...formData, procedure_type: e.target.value })}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                >
                                    {PROCEDURE_TYPES.map(type => (
                                        <option key={type.value} value={type.value}>{type.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    rows={2}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    placeholder="Description of this procedure..."
                                />
                            </div>

                            <div className="flex items-center gap-2 pt-1">
                                <input
                                    type="checkbox"
                                    id="procedure_is_stock_tracked"
                                    checked={(formData as any).is_stock_tracked}
                                    onChange={(e) => setFormData({ ...(formData as any), is_stock_tracked: e.target.checked } as any)}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="procedure_is_stock_tracked" className="text-sm font-medium text-gray-700">
                                    Track as Inventory (stock-tracked)
                                </label>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Default Duration (minutes)</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={formData.default_duration_minutes}
                                    onChange={(e) => setFormData({ ...formData, default_duration_minutes: parseInt(e.target.value) || 15 })}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                />
                            </div>

                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="requires_equipment"
                                    checked={formData.requires_equipment}
                                    onChange={(e) => setFormData({ ...formData, requires_equipment: e.target.checked })}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="requires_equipment" className="text-sm font-medium text-gray-700">
                                    Requires Special Equipment
                                </label>
                            </div>

                            {formData.requires_equipment && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Equipment Notes</label>
                                    <input
                                        type="text"
                                        value={formData.equipment_notes}
                                        onChange={(e) => setFormData({ ...formData, equipment_notes: e.target.value })}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        placeholder="e.g., X-ray machine required"
                                    />
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Internal Notes</label>
                                <textarea
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    rows={2}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    placeholder="Notes for staff..."
                                />
                            </div>

                            {/* Delete Section - only for custom items in edit mode */}
                            {canHardDelete && (
                                <div className="border-t border-gray-200 pt-6 mt-6">
                                    {loadingRefs ? (
                                        <div className="flex items-center gap-2 text-gray-500">
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-gray-600"></div>
                                            <span className="text-sm">Checking usage...</span>
                                        </div>
                                    ) : hasReferences ? (
                                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                            <div className="flex items-start gap-3">
                                                <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                                <div>
                                                    <h3 className="text-sm font-medium text-amber-800">
                                                        This item is currently in use
                                                    </h3>
                                                    <p className="text-sm text-amber-700 mt-1">
                                                        {references?.procedure_orders && `${references.procedure_orders} order${references.procedure_orders > 1 ? 's' : ''}`}
                                                        {' '}reference this item.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h3 className="text-sm font-medium text-gray-900">
                                                        Delete this item
                                                    </h3>
                                                    <p className="text-sm text-gray-500 mt-0.5">
                                                        Permanently remove this custom procedure and its linked billing item
                                                    </p>
                                                </div>

                                                {!showDeleteConfirm ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowDeleteConfirm(true)}
                                                        className="px-3 py-1.5 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                                                    >
                                                        Delete
                                                    </button>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={handleHardDelete}
                                                            disabled={deleting}
                                                            className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
                                                        >
                                                            {deleting ? 'Deleting...' : 'Confirm'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowDeleteConfirm(false)}
                                                            disabled={deleting}
                                                            className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:col-start-2 sm:text-sm"
                                >
                                    {loading ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProcedureCatalogItemModal;

